import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Package2, RefreshCw, Search } from "lucide-react";
import React from "react";
import { toast } from "react-hot-toast";
import { Button } from "../../components/ui/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/Card";
import { Input } from "../../components/ui/Input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { fetchWithAuth } from "../../hooks/useAuth";

type StringOrNumber = string | number;

// Removed aggregated product view; only per-warehouse stocks are displayed

interface StocksListEntryProduct {
  measuringUnit: string;
  productCode?: StringOrNumber;
  productName: string;
  quantity: string | number;
}

interface StocksListEntry {
  products: StocksListEntryProduct[];
  warehouse: {
    warehouseName: string;
    warehouseType: string; // 'en detail' | 'en gros'
  };
}

export function ProductsStockPannel() {
  const queryClient = useQueryClient();

  const [date, setDate] = React.useState<string>(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [searchName, setSearchName] = React.useState<string>("");
  const [productCode, setProductCode] = React.useState<string>("");
  const [showStocks, setShowStocks] = React.useState<boolean>(false);

  const params = React.useMemo(() => {
    const p: Record<string, string> = { date };
    if (searchName) p.productName = searchName;
    if (productCode) p.productCode = productCode;
    return p;
  }, [date, searchName, productCode]);

  // Aggregated query removed per request

  const { data: entries, isLoading: entriesLoading } = useQuery({
    queryKey: ["stocks", params],
    queryFn: async () => {
      const qs = new URLSearchParams(params).toString();
      const res = await fetchWithAuth(
        `/api/admin/stocks?${qs}&allWarehouses=true`
      );
      if (!res.ok)
        throw new Error("Eroare la încărcarea stocurilor (pe depozite)");
      const json = await res.json();
      return (json?.stocks ?? []) as StocksListEntry[];
    },
    enabled: Boolean(date) && showStocks,
  });

  const syncStocks = useMutation({
    mutationFn: async (payload: { date: string }) => {
      const res = await fetchWithAuth(`/api/admin/stocks/sync`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Eroare la sincronizarea stocurilor");
      }
      return (await res.json()) as {
        updatedCount: number;
        changes: Array<{
          id: number;
          cod: number;
          name: string;
          newStock: number;
          oldStock: number;
          diff: number;
        }>;
      };
    },
    onSuccess: (data) => {
      toast.success(
        `Sincronizare reușită: ${data.updatedCount} produse actualizate`
      );
      queryClient.invalidateQueries({ queryKey: ["stocks"] });
      queryClient.invalidateQueries({ queryKey: ["stocks-aggregated"] });
      // Optional: log details for debugging
      try {
        // eslint-disable-next-line no-console
        console.table(
          data.changes.map((c) => ({
            id: c.id,
            cod: c.cod,
            name: c.name,
            from: c.oldStock,
            to: c.newStock,
            diff: c.diff,
          }))
        );
      } catch {}
    },
    onError: (e: any) => toast.error(e?.message || "Eroare la sincronizare"),
  });

  return (
    <Card className="bg-white shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package2 className="w-5 h-5 text-blue-600" />
            Stocuri (SmartBill)
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-gray-500" />
              <Input
                className="pl-9"
                placeholder="Caută după nume produs"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
              />
            </div>
            <Input
              placeholder="Cod produs"
              value={productCode}
              onChange={(e) => setProductCode(e.target.value)}
            />
            {!showStocks ? (
              <Button
                variant="outline"
                onClick={() => setShowStocks(true)}
                disabled={entriesLoading}
              >
                Arată stocurile
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => setShowStocks(false)}
                disabled={entriesLoading}
              >
                Ascunde stocurile
              </Button>
            )}
            <Button
              onClick={() => syncStocks.mutate({ date })}
              disabled={syncStocks.isPending}
              className="flex items-center gap-2"
            >
              <RefreshCw
                className={`w-4 h-4 ${
                  syncStocks.isPending ? "animate-spin" : ""
                }`}
              />
              {syncStocks.isPending
                ? "Sincronizare..."
                : "Sincronizează stocurile"}
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {!showStocks ? (
          <div className="text-gray-600 text-sm">
            Apasă "Arată stocurile" pentru a încărca stocurile.
          </div>
        ) : (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              Pe depozite
            </h3>
            {entriesLoading ? (
              <div className="text-gray-500">Se încarcă...</div>
            ) : (entries?.length ?? 0) === 0 ? (
              <div className="text-gray-500">Nu există rezultate</div>
            ) : (
              <div className="space-y-4">
                {(entries ?? []).map((e, idx) => (
                  <Card key={idx} className="border">
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm font-semibold text-gray-800">
                        {e.warehouse.warehouseName} ({e.warehouse.warehouseType}
                        )
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-gray-50">
                              <TableHead className="font-semibold">
                                Cod
                              </TableHead>
                              <TableHead className="font-semibold">
                                Produs
                              </TableHead>
                              <TableHead className="font-semibold">
                                Cantitate
                              </TableHead>
                              <TableHead className="font-semibold">
                                UM
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {e.products.map((p) => (
                              <TableRow
                                key={(
                                  p.productCode ?? p.productName
                                ).toString()}
                                className="hover:bg-gray-50"
                              >
                                <TableCell>{p.productCode ?? "-"}</TableCell>
                                <TableCell className="font-medium text-gray-900">
                                  {p.productName}
                                </TableCell>
                                <TableCell>{p.quantity}</TableCell>
                                <TableCell>{p.measuringUnit}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ProductsStockPannel;
